import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import StatusDot from "./StatusDot";
import NavRow from "./NavRow";
import AccountMenu from "./AccountMenu";
import { BurgerIcon } from "./icons";
import { visibleNavItems } from "./nav";
import { useAuth } from "../hooks/useAuth";
import type { AppStatus } from "../hooks/useAppStatus";

/**
 * Phone navigation: a top bar with a burger, and the drawer behind it.
 *
 * Both are hidden from `md` up, where the sidebar is permanently on screen and
 * a burger would be a button that hides something already visible.
 *
 * The drawer stays mounted and slides on a transform rather than mounting and
 * unmounting: that is what buys the closing animation for free, and a
 * transform-only transition stays on the compositor. When it is shut it is
 * `inert`, so its seven links are not in the tab order and not in the
 * accessibility tree.
 *
 * The header sits above the drawer in the stacking order on purpose — the
 * burger stays where the thumb left it and turns into the close button.
 */
export default function TopBar({ status }: { status: AppStatus }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const burgerRef = useRef<HTMLButtonElement>(null);
  const firstLinkRef = useRef<HTMLAnchorElement>(null);

  // Route changed — the menu has done its job.
  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return;
    firstLinkRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      burgerRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <header className="jv-chrome relative z-50 flex h-14 shrink-0 items-center gap-2.5 border-b border-line px-3 md:hidden">
        <button
          ref={burgerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="jv-nav"
          aria-label={open ? "Закрити меню" : "Меню"}
          className={`grid size-9 shrink-0 place-items-center rounded-control transition-colors ${
            open ? "bg-surface-2 text-fg" : "text-muted hover:bg-surface-2 hover:text-fg"
          }`}
        >
          <BurgerIcon open={open} />
        </button>

        <span
          aria-hidden="true"
          className="grid size-7 shrink-0 place-items-center rounded-control bg-accent/15 font-mono text-sm font-semibold text-accent"
        >
          J
        </span>
        <span className="text-sm font-semibold tracking-tight">Jarvis</span>

        <span className="jv-compact ml-auto flex min-w-0 items-center gap-1.5 text-micro text-muted">
          <StatusDot tone={status.tone} pulse={status.tone === "ok"} />
          <span className="truncate">{status.statusText}</span>
        </span>
      </header>

      <NavDrawer
        open={open}
        onClose={() => setOpen(false)}
        status={status}
        firstLinkRef={firstLinkRef}
      />
    </>
  );
}

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  status: AppStatus;
  firstLinkRef: React.Ref<HTMLAnchorElement>;
}

function NavDrawer({ open, onClose, status, firstLinkRef }: NavDrawerProps) {
  const { user } = useAuth();
  const items = visibleNavItems(user?.is_owner ?? false);

  return (
    <div
      id="jv-nav"
      inert={!open}
      className={`fixed inset-x-0 top-14 bottom-0 z-40 md:hidden ${
        open ? "" : "pointer-events-none"
      }`}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="Закрити меню"
        onClick={onClose}
        className={`absolute inset-0 h-full w-full bg-bg/60 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      <nav
        aria-label="Розділи"
        // The elevated recipe (bg-elev + hairline + shadow-2) written out rather
        // than `jv-elevated`, because this panel is full-bleed: it must keep the
        // right-hand edge and drop the radius and the three off-screen borders
        // the utility would add.
        className={`absolute inset-y-0 left-0 flex w-64 max-w-[82vw] flex-col border-r border-line bg-elev shadow-2 transition-transform duration-300 ease-drawer ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <ul className="flex-1 overflow-y-auto p-2">
          {items.map((item, index) => (
            <li key={item.to}>
              <NavRow
                item={item}
                linkRef={index === 0 ? firstLinkRef : undefined}
                // Items trail the panel in, one after another. The delay only
                // applies on the way in; on the way out they leave together
                // with the panel, which is what a dismissal should feel like.
                style={{ transitionDelay: open ? `${70 + index * 30}ms` : "0ms" }}
                className={open ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0"}
              />
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between gap-2 border-t border-line px-4 py-3">
          <StatusDot
            tone={status.tone}
            pulse={status.tone === "ok"}
            label={status.statusText}
            title={status.statusTitle || status.statusText}
            className="min-w-0"
          />
          <AccountMenu />
        </div>
      </nav>
    </div>
  );
}
